/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { css, js } from '@ingresse/injector';
import { useEffect, useState } from 'react';

import { Environment } from '../constants/environments';
import { useEventContext } from '../hooks/contexts/event.context';
import { User } from '../types/user';
import { logger } from '../utils/logger';
import { useUserContext } from './contexts/user.context';

declare global { interface Window { Authing: any }}

const baseUrlCdn = '//cdn.ingresse.com';
const elementId = 'auth-container';
const cssInject = { id : 'auth-css', href : `${baseUrlCdn}/auth/auth.min.css` };
const jsInject = { id : 'auth-js', src : `${baseUrlCdn}/auth/auth.min.js` };
const sdkInject = { id : 'sdk-js', src : `${baseUrlCdn}/sdk/ingresse-sdk.js` };

const useAuthing = (path: string) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const { event } = useEventContext();

  const { updateUser, handleAuthenticatedUser } = useUserContext();

  const inject = () => {
    css(cssInject);
    js(jsInject);
    js(sdkInject);
  };

  const resolveEnvironment = (env: string) => {
    const authUrl: Record<string, string> = {
      homologation : 'HML',
    };

    if (env.startsWith('uat')) {
      return 'UAT';
    }

    return authUrl[env] || '';
  };

  /**
   * useEffect hook to initialize the Authing object when the script is loaded and the path changes.
   * This hook runs whenever the `scriptLoaded` or `path` state changes and performs the following actions:
   * - Defines a callback function `onLoadJSCallback` that initializes the Authing object if the script is loaded.
   * - Determines if the environment is in the homologation stage.
   * - Sets the options for the Authing object based on the environment.
   * - Calls the `setInitialized` function to initialize the Authing object with the specified options and element ID.
   * - Calls the `inject` function to inject the necessary script.
   * - Executes the `onLoadJSCallback` function.
   * - Catches and logs any errors that occur during the process.
   *
   * @param scriptLoaded - Boolean state indicating if the script is loaded.
   * @param path - The current path for the Authing options.
  */
  useEffect(() => {
    try {
      const onLoadJSCallback = () => {
        if (scriptLoaded) {
          const inStage = String(Environment.NODE_ENV) === 'homologation';

          const options = {
            api : inStage ? Environment.ENVIRONMENT : '',
            env : inStage ? Environment.ENVIRONMENT : '',
            host : resolveEnvironment(Environment.ENVIRONMENT),
            app : 'embedstore',
            companyId : 1,
            path : path,
            apikey : Environment.API_KEY,
            simpleRegistration : event?.attributes.simpleRegistration?.value ?? false,
          };

          setInitialized(new window.Authing(
            options,
            {
              done : ({ content : user }: { content: User }) => {
                updateUser(user);
                setLoggedIn(Boolean(user));
                handleAuthenticatedUser(Boolean(user));
              }
            }
            , elementId)
          );
        }
      };
      inject();
      onLoadJSCallback();
    } catch (error) {
      logger.error(error as string);
    }
  }, [scriptLoaded, path]);

  /**
   * useEffect hook to check if the Authing script has been loaded.
   * This hook runs once when the component mounts and performs the following actions:
   * - Defines a function `checkIfScriptLoaded` that checks if `window.Authing` is defined.
   *   - If `window.Authing` is defined, it sets the `scriptLoaded` state to true.
   * - Sets an interval to run the `checkIfScriptLoaded` function every 500 milliseconds.
   * - Cleans up the interval when the component unmounts.
  */
  useEffect(() => {
    const checkIfScriptLoaded = () => {
      if (window.Authing) setScriptLoaded(true);
    };

    const interval = setInterval(checkIfScriptLoaded, 500);

    return () => clearInterval(interval);
  }, []);

  return { elementId, initialized, loggedIn };
};

export default useAuthing;
